<template>
  <div class="pr-root" @click="onDocClick">
    <!-- ── Header ─────────────────────────────────────────── -->
    <div class="pr-header">
      <div class="pr-hd-left">
        <nav class="pr-breadcrumb">
          Reports <span class="pr-bc-chevron">›</span> Projects
        </nav>
        <div class="pr-hd-row">
          <h1 class="pr-page-title">Projects Report</h1>
          <span class="pr-project-count" v-if="!is_loading">
            {{ filteredData.length.toLocaleString() }} project{{
              filteredData.length !== 1 ? "s" : ""
            }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Body ───────────────────────────────────────────── -->
    <div class="pr-body">
      <!-- KPI Strip -->
      <div class="pr-kpi-row">
        <div v-for="kpi in kpiCards" :key="kpi.label" class="pr-kpi-card">
          <span
            class="pr-kpi-accent"
            :style="{ background: kpi.accent }"
          ></span>
          <div class="pr-kpi-label">{{ kpi.label }}</div>
          <div class="pr-kpi-value">{{ kpi.value }}</div>
          <div class="pr-kpi-sub">{{ kpi.sub }}</div>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="pr-toolbar">
        <!-- Search -->
        <div class="pr-search-wrap">
          <svg
            class="pr-search-icon"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            class="pr-search"
            v-model="search"
            placeholder="Search projects, developers…"
            @click.stop
          />
          <button
            v-if="search"
            class="pr-search-clear"
            @click.stop="search = ''"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Status segmented tabs -->
        <div class="pr-seg">
          <button
            v-for="tab in statusTabs"
            :key="tab.key"
            class="pr-seg-btn"
            :class="{ active: statusTab === tab.key }"
            @click="statusTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Type dropdown -->
        <div class="pr-dd" @click.stop>
          <button class="pr-dd-btn" @click="typeOpen = !typeOpen">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="14" y2="12" />
              <line x1="4" y1="18" x2="10" y2="18" />
            </svg>
            {{ typeFilter }}
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <div v-if="typeOpen" class="pr-dd-menu">
            <button
              v-for="t in typeOptions"
              :key="t"
              class="pr-dd-item"
              :class="{ active: typeFilter === t }"
              @click="
                typeFilter = t;
                typeOpen = false;
              "
            >
              {{ t }}
            </button>
          </div>
        </div>

        <div class="pr-toolbar-spacer"></div>

        <!-- Selection badge -->
        <div v-if="selectedCount > 0" class="pr-sel-badge">
          <span class="pr-sel-num">{{ selectedCount }}</span> selected
          <button class="pr-sel-compare">Compare</button>
        </div>

        <!-- Export -->
        <button class="pr-btn-ghost">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>
      </div>

      <!-- Table -->
      <div class="pr-table-wrap">
        <div v-if="is_loading" class="pr-loading-state">
          <div class="pr-spinner"></div>
          <span>Loading projects…</span>
        </div>
        <div v-show="!is_loading" ref="tabulatorEl" class="pr-tabulator"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { wrapColumns } from "../utilities/tabulatorSorter";
import "tabulator-tables/dist/css/tabulator.min.css";
import { BACKEND_URL } from "../variables.js";
import { cloneDeep } from "lodash";

const STATUS_MAP = {
  now_selling: {
    label: "Now Selling",
    bg: "rgba(31,157,85,0.10)",
    fg: "#1F7A45",
    dot: "#1F9D55",
  },
  selling_soon: {
    label: "Selling Soon",
    bg: "rgba(255,184,28,0.14)",
    fg: "#8A6A00",
    dot: "#FFB81C",
  },
  in_planning: {
    label: "In Planning",
    bg: "rgba(52,51,118,0.10)",
    fg: "#343376",
    dot: "#343376",
  },
  sold_out: {
    label: "Sold Out",
    bg: "rgba(84,84,84,0.10)",
    fg: "#545454",
    dot: "#8A8A8A",
  },
  sold_out_2: {
    label: "Sold Out",
    bg: "rgba(84,84,84,0.10)",
    fg: "#545454",
    dot: "#8A8A8A",
  },
  new_leasing: {
    label: "Now Leasing",
    bg: "rgba(31,157,85,0.10)",
    fg: "#1F7A45",
    dot: "#1F9D55",
  },
  leased_out: {
    label: "Leased Out",
    bg: "rgba(84,84,84,0.10)",
    fg: "#545454",
    dot: "#8A8A8A",
  },
  leasing_soon: {
    label: "Leasing Soon",
    bg: "rgba(255,184,28,0.14)",
    fg: "#8A6A00",
    dot: "#FFB81C",
  },
};

const TYPE_ICONS = {
  Apartment: "&#xe800;",
  "Mixed-Use Apartment": "&#xe800;",
  Townhome: "&#xe801;",
  "Stacked Townhome": "&#xe801;",
  Commercial: "&#xe802;",
  Industrial: "&#xe803;",
  "Row Home": "&#xe804;",
  Podium: "&#xe805;",
};

const TYPE_COLORS = {
  Apartment: "#EDF2FE",
  "Mixed-Use Apartment": "#EDF2FE",
  Townhome: "#FEF3E2",
  "Stacked Townhome": "#FEF3E2",
  Commercial: "#F0FDF4",
  Industrial: "#FFF1F2",
  "Row Home": "#F5F0FF",
  Podium: "#FFF7ED",
};

function fmtMoney(v) {
  const n = parseFloat(v);
  if (!v || isNaN(n) || n === 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function fmtNum(v) {
  const n = parseFloat(v);
  return !v || isNaN(n) || n === 0
    ? "—"
    : Math.round(n).toLocaleString("en-CA");
}

function dash() {
  return '<span style="color:#C9CCD2">—</span>';
}

export default {
  name: "ProjectsReport",

  data() {
    return {
      query: this.$route.query,
      is_loading: true,
      reportDetail: [],
      reportFields: ["project_type", "developer"],
      search: "",
      statusTab: "",
      typeFilter: "All Types",
      typeOpen: false,
      selectedCount: 0,
      tabulator: null,
    };
  },

  computed: {
    statusTabs() {
      return [
        { key: "", label: "All" },
        { key: "now_selling", label: "Now Selling" },
        { key: "selling_soon", label: "Selling Soon" },
        { key: "in_planning", label: "In Planning" },
        { key: "sold_out", label: "Sold Out" },
      ];
    },

    typeOptions() {
      const types = [
        "All Types",
        ...new Set(
          this.reportDetail.map((r) => r.project_type).filter(Boolean)
        ),
      ].sort();
      return types;
    },

    filteredData() {
      let rows = this.reportDetail;
      if (this.search) {
        const q = this.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.developer || "").toLowerCase().includes(q) ||
            (r.city || "").toLowerCase().includes(q)
        );
      }
      if (this.statusTab) {
        rows = rows.filter((r) => r.status === this.statusTab);
      }
      if (this.typeFilter !== "All Types") {
        rows = rows.filter((r) => r.project_type === this.typeFilter);
      }
      return rows;
    },

    kpis() {
      const d = this.filteredData;
      const total = d.length;
      const nowSelling = d.filter((r) => r.status === "now_selling").length;
      const totalUnits = d.reduce(
        (s, r) => s + (parseInt(r.total_units) || 0),
        0
      );
      const soldUnits = d.reduce(
        (s, r) => s + (parseInt(r.sold_units) || 0),
        0
      );
      const psfVals = d
        .map((r) => parseFloat(r.total_avg_psf))
        .filter((n) => n > 0);
      const avgPsf = psfVals.length
        ? Math.round(psfVals.reduce((a, b) => a + b, 0) / psfVals.length)
        : 0;
      return { total, nowSelling, totalUnits, soldUnits, avgPsf };
    },

    kpiCards() {
      const k = this.kpis;
      const sellThrough = k.totalUnits
        ? Math.round((k.soldUnits / k.totalUnits) * 100)
        : 0;
      return [
        {
          label: "Total Projects",
          value: k.total.toLocaleString(),
          sub: "Across active portfolio",
          accent: "#12283F",
        },
        {
          label: "Now Selling",
          value: k.nowSelling.toLocaleString(),
          sub: `${
            k.total ? Math.round((k.nowSelling / k.total) * 100) : 0
          }% of portfolio`,
          accent: "#1F9D55",
        },
        {
          label: "Total Inventory",
          value: k.totalUnits.toLocaleString(),
          sub: `${k.soldUnits.toLocaleString()} sold · ${sellThrough}% sell-through`,
          accent: "#FC5868",
        },
        {
          label: "Avg $ / sqft",
          value: k.avgPsf ? `$${k.avgPsf.toLocaleString()}` : "—",
          sub: "Weighted by listed units",
          accent: "#343376",
        },
      ];
    },
  },

  watch: {
    filteredData(rows) {
      if (this.tabulator) this.tabulator.setData(rows);
    },
  },

  methods: {
    numberWithCommas(x) {
      return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : x;
    },

    statusLabel(key) {
      return (STATUS_MAP[key] || {}).label || key || "—";
    },

    onDocClick() {
      this.typeOpen = false;
    },

    buildColumns() {
      const checkboxCol = {
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        hozAlign: "center",
        headerHozAlign: "center",
        width: 40,
        minWidth: 40,
        resizable: false,
        headerSort: false,
        cellClick(e, cell) {
          cell.getRow().toggleSelect();
        },
      };

      const projectCol = {
        title: "Project",
        field: "name",
        minWidth: 220,
        widthGrow: 2.5,
        sorter: "string",
        formatter: (cell) => {
          const row = cell.getRow().getData();
          const name = row.name || "";
          const dev = row.developer || "";
          const href = row.ref_link || "#";
          const type = row.project_type || "";
          const bg = TYPE_COLORS[type] || "#EDF2FE";
          const letter = (type || "P")[0].toUpperCase();
          return `<div class="pr-proj-cell">
            <div class="pr-proj-thumb" style="background:${bg}">${letter}</div>
            <div class="pr-proj-info">
              <a href="${href}" class="pr-proj-name" target="_blank">${name}</a>
              <div class="pr-proj-dev">${dev}</div>
            </div>
          </div>`;
        },
      };

      const typeCol = {
        title: "Type",
        field: "project_type",
        width: 150,
        sorter: "string",
        formatter: (cell) => {
          const v = cell.getValue();
          return v ? `<span class="pr-type-tag">${v}</span>` : dash();
        },
      };

      const statusCol = {
        title: "Status",
        field: "status",
        width: 140,
        sorter: "string",
        formatter: (cell) => {
          const s = cell.getValue() || "";
          const c = STATUS_MAP[s] || STATUS_MAP.in_planning;
          return `<span class="pr-status-pill" style="background:${c.bg};color:${c.fg}">
            <span class="pr-status-dot" style="background:${c.dot}"></span>${c.label}
          </span>`;
        },
      };

      const storeyCol = {
        title: "Fl",
        field: "storey",
        width: 56,
        hozAlign: "right",
        headerHozAlign: "right",
        sorter: "number",
        formatter: (cell) => {
          const v = parseInt(cell.getValue());
          return v > 0 ? v : dash();
        },
      };

      const salesDateCol = {
        title: "Sales Start",
        field: "sales_start_date",
        width: 120,
        sorter: "date",
        sorterParams: { format: "YYYY-MM-DD" },
        formatter: (cell) => {
          const v = cell.getValue();
          if (!v) return dash();
          const d = new Date(v + "T00:00:00");
          return isNaN(d)
            ? v
            : d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              });
        },
      };

      const soldCol = {
        title: "Sold / Total",
        field: "sold_units",
        minWidth: 130,
        widthGrow: 1,
        sorter: "number",
        formatter: (cell) => {
          const row = cell.getRow().getData();
          const sold = parseInt(row.sold_units) || 0;
          const total = parseInt(row.total_units) || 0;
          if (!total) return dash();
          const pct = Math.round((sold / total) * 100);
          return `<div class="pr-sold-wrap">
            <div class="pr-sold-row">
              <span class="pr-sold-nums">${sold.toLocaleString()}<span class="pr-sold-of">/${total.toLocaleString()}</span></span>
              <span class="pr-sold-pct">${pct}%</span>
            </div>
            <div class="pr-sold-track"><div class="pr-sold-bar" style="width:${Math.max(
              2,
              pct
            )}%"></div></div>
          </div>`;
        },
      };

      const psfCol = {
        title: "$/sqft",
        field: "total_avg_psf",
        width: 90,
        hozAlign: "right",
        headerHozAlign: "right",
        sorter: "number",
        formatter: (cell) => {
          const v = parseFloat(cell.getValue());
          return v > 0 ? `$${Math.round(v).toLocaleString("en-CA")}` : dash();
        },
      };

      const priceCol = {
        title: "Avg Price",
        field: "total_avg_price",
        width: 110,
        hozAlign: "right",
        headerHozAlign: "right",
        sorter: "number",
        formatter: (cell) => {
          const v = parseFloat(cell.getValue());
          return v > 0
            ? `<span class="pr-price">${fmtMoney(v)}</span>`
            : dash();
        },
      };

      const cityCol = {
        title: "Region",
        field: "city",
        minWidth: 120,
        widthGrow: 1,
        sorter: "string",
        formatter: (cell) => cell.getValue() || dash(),
      };

      // Extra columns from report[] query params
      const extraCols = this.reportFields
        .filter((f) => !["project_type", "developer"].includes(f))
        .map((f) => ({
          title: this.fieldSwitch(f),
          field: f,
          minWidth: 90,
          sorter: "string",
          formatter: (cell) => {
            const v = cell.getValue();
            return v != null && v !== "" ? String(v) : dash();
          },
        }));

      return [
        checkboxCol,
        projectCol,
        typeCol,
        statusCol,
        storeyCol,
        salesDateCol,
        soldCol,
        psfCol,
        priceCol,
        cityCol,
        ...extraCols,
      ];
    },

    initTabulator() {
      if (!this.$refs.tabulatorEl) return;
      if (this.tabulator) {
        this.tabulator.destroy();
        this.tabulator = null;
      }

      this.tabulator = new Tabulator(this.$refs.tabulatorEl, {
        data: this.filteredData,
        layout: "fitDataStretch",
        height: "100%",
        placeholder: '<div class="pr-placeholder">No projects found</div>',
        reactiveData: false,
        rowHeight: 52,
        pagination: true,
        paginationSize: 20,
        paginationSizeSelector: [10, 20, 50, 100],
        columns: wrapColumns(this.buildColumns()),
        rowFormatter: (row) => {
          row.getElement().classList.add("pr-row");
        },
      });

      const syncSel = () => {
        this.selectedCount = this.tabulator
          ? this.tabulator.getSelectedRows().length
          : 0;
      };
      this.tabulator.on("rowSelected", syncSel);
      this.tabulator.on("rowDeselected", syncSel);
      this.tabulator.on("rowClick", (e, row) => {
        const link = row.getData().ref_link;
        if (link && !e.target.closest("a")) this.$router.push(link);
      });
    },

    fieldSwitch(field) {
      const map = {
        project_type: "Type & Storey",
        general_unit_type: "Unit Type",
        rezoning_status: "Rezoning Status",
        dp_status: "DP Status",
        bp_status: "BP Status",
        floor_area: "Floor Area",
        units_utc_da: "Units DA",
        units_utc: "Units",
        construction_status: "Construction Status",
        presale_name: "Presale Name",
        presale_lot_area: "Total Lot Area (Sqft)",
        nid: "Page ID",
        status: "Project Status",
        developer: "Developer",
        current_avg_psf: "Current Avg $PSF",
        sold_units: "Sold Units",
        sales_start_date: "Sales Start",
        project_market: "Project Marketing",
        general_contractor: "General Contractor",
        construction_start_date: "Construction Start",
        construction_complete_date: "Construction Complete",
        total_units: "Total Units",
        rlsd_units: "Rlsd Units",
        available: "Avail Units",
        total_psf: "Total PSF",
        total_sold_units: "Sold Units",
        total_avg_size: "Avg Size",
        sold_out_date: "Sales Finish",
        total_avg_psf: "Avg $PSF",
        total_avg_price: "Avg Price",
        updated: "Updated",
        current_avg_size: "Current Avg Size",
        current_avg_price: "Current Avg Price",
        storey: "Storey",
        application_stage: "Application Stage",
        application_date: "Application Date",
      };
      return map[field] || field;
    },

    getReportDetails(params) {
      const uids = JSON.parse(localStorage.getItem("uids"));
      const endpoint = `${BACKEND_URL}api/get_projects_report_utc?${params}`;
      this.is_loading = true;

      const onData = (data) => {
        this.is_loading = false;
        if (!Array.isArray(data)) return;
        this.reportDetail = cloneDeep(data);
        this.$nextTick(() => {
          this.initTabulator();
        });
      };

      if (uids) {
        $.get(endpoint, { uids }, (data, status) => {
          if (status === "success") onData(data);
          this.is_loading = false;
        });
      } else {
        $.get({ url: endpoint }, (data, status) => {
          if (status === "success") onData(data);
          this.is_loading = false;
        });
      }
    },
  },

  mounted() {
    const vm = this;
    let param = "";

    const pushType = (val) => {
      param += `type[]=${val}&`;
    };
    const pushCity = (val) => {
      param += `city[]=${val}&`;
    };

    // Build query params from route query (same logic as original)
    const q = this.query;

    if ($.isArray(q["type[]"])) q["type[]"].forEach(pushType);
    else if (q["type[]"]) pushType(q["type[]"]);

    if ($.isArray(q["city[]"])) q["city[]"].forEach(pushCity);
    else if (q["city[]"]) pushCity(q["city[]"]);

    ["constructionstatus[]", "unittype[]"].forEach((key) => {
      if ($.isArray(q[key]))
        q[key].forEach((v) => {
          param += `${key}=${v}&`;
        });
      else if (q[key]) param += `${key}=${q[key]}&`;
    });

    [
      "rzSubDate",
      "rzAppDate",
      "dpSubDate",
      "dpIssueDate",
      "bpIssueDate",
      "rzStatus",
      "bpStatus",
      "dpStatus",
    ].forEach((d) => {
      const k = d + "[]";
      if ($.isArray(q[k]))
        q[k].forEach((v) => {
          param += `${d}[]=${v}&`;
        });
      else if (q[k]) param += `${d}[]=${q[k]}&`;
    });

    if (q["search"]) param += `search=${q["search"]}&`;
    if (q["developer"]) param += `developer=${q["developer"]}&`;

    if ($.isArray(q["status[]"]))
      q["status[]"].forEach((v) => {
        param += `status[]=${v}&`;
      });
    else if (q["status[]"]) param += `status[]=${q["status[]"]}`;

    if ($.isArray(q["report[]"])) {
      q["report[]"].forEach((a) => {
        if (
          ![
            "project_report",
            "current_availability",
            "planning_report",
          ].includes(a)
        ) {
          this.reportFields.push(a);
        }
      });
    } else if (
      q["report[]"] &&
      !["project_report", "current_availability", "planning_report"].includes(
        q["report[]"]
      )
    ) {
      this.reportFields = [q["report[]"]];
    }

    this.getReportDetails(param);
  },

  beforeDestroy() {
    if (this.tabulator) {
      this.tabulator.destroy();
      this.tabulator = null;
    }
  },
};
</script>

<style scoped>
/* ── Root ───────────────────────────────────────────────────── */
.pr-root {
  min-height: 100vh;
  background: #f4f5f7;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ── Header ─────────────────────────────────────────────────── */
.pr-header {
  background: #fff;
  border-bottom: 1px solid #e9e8e9;
  padding: 14px 24px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: sticky;
  top: 0;
  z-index: 50;
}
.pr-breadcrumb {
  font-size: 11px;
  color: #8a8a8a;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 3px;
}
.pr-bc-chevron {
  color: #c9ccd2;
}
.pr-hd-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.pr-page-title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: #12283f;
  margin: 0;
  line-height: 1.2;
}
.pr-project-count {
  font-size: 11px;
  color: #8a8a8a;
}

/* ── Body ───────────────────────────────────────────────────── */
.pr-body {
  padding: 18px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ── KPI Strip ──────────────────────────────────────────────── */
.pr-kpi-row {
  display: flex;
  gap: 10px;
}
.pr-kpi-card {
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
  overflow: hidden;
}
.pr-kpi-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 26px;
  border-radius: 0 0 2px 0;
}
.pr-kpi-label {
  font-size: 10px;
  color: #545454;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 500;
}
.pr-kpi-value {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 24px;
  letter-spacing: -0.02em;
  color: #12283f;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.pr-kpi-sub {
  font-size: 11px;
  color: #8a8a8a;
  line-height: 1.4;
}

/* ── Toolbar ────────────────────────────────────────────────── */
.pr-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
  flex-wrap: wrap;
}
.pr-toolbar-spacer {
  flex: 1;
}

/* Search */
.pr-search-wrap {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  background: #fafbfd;
  width: 240px;
  min-width: 0;
}
.pr-search-icon {
  color: #8a8a8a;
  flex-shrink: 0;
}
.pr-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #12283f;
  font-family: inherit;
  min-width: 0;
}
.pr-search::placeholder {
  color: #8a8a8a;
}
.pr-search-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: #8a8a8a;
  padding: 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Status segmented tabs */
.pr-seg {
  display: flex;
  gap: 0;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  padding: 2px;
  background: #fafbfd;
}
.pr-seg-btn {
  padding: 5px 10px;
  border: none;
  background: transparent;
  color: #545454;
  font-size: 11px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 120ms ease;
  font-family: inherit;
}
.pr-seg-btn.active {
  background: #12283f;
  color: #fff;
  font-weight: 600;
}

/* Type dropdown */
.pr-dd {
  position: relative;
}
.pr-dd-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  font-size: 12px;
  color: #12283f;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
}
.pr-dd-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(18, 40, 63, 0.1);
  padding: 4px;
  min-width: 160px;
}
.pr-dd-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 7px 10px;
  background: transparent;
  color: #12283f;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: background 80ms;
}
.pr-dd-item:hover {
  background: #f4f5f7;
}
.pr-dd-item.active {
  background: #edf2fe;
  color: #343376;
  font-weight: 600;
}

/* Selection badge */
.pr-sel-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #12283f;
  background: #edf2fe;
  padding: 5px 10px;
  border-radius: 6px;
}
.pr-sel-num {
  font-weight: 700;
}
.pr-sel-compare {
  background: none;
  border: none;
  color: #343376;
  font-size: 11px;
  cursor: pointer;
  text-decoration: underline;
  font-family: inherit;
  padding: 0;
}

/* Export / ghost button */
.pr-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #c9ccd2;
  border-radius: 6px;
  font-size: 11px;
  color: #12283f;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  font-weight: 600;
  transition: background 120ms;
}
.pr-btn-ghost:hover {
  background: #f4f5f7;
}

/* ── Table Wrapper ──────────────────────────────────────────── */
.pr-table-wrap {
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
  overflow: hidden;
  min-height: 400px;
  flex: 1;
}
.pr-tabulator {
  height: 100%;
  min-height: 400px;
}

/* Loading state */
.pr-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  color: #8a8a8a;
  font-size: 13px;
}
.pr-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(18, 40, 63, 0.1);
  border-top-color: #12283f;
  border-radius: 50%;
  animation: pr-spin 0.75s linear infinite;
}
@keyframes pr-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile responsive */
@media (max-width: 768px) {
  .pr-body {
    padding: 12px 14px 20px;
    gap: 10px;
  }
  .pr-header {
    padding: 12px 14px;
  }
  .pr-kpi-row {
    flex-wrap: wrap;
  }
  .pr-kpi-card {
    min-width: calc(50% - 5px);
  }
  .pr-kpi-value {
    font-size: 20px;
  }
  .pr-toolbar {
    gap: 6px;
  }
  .pr-search-wrap {
    width: 100%;
  }
  .pr-seg {
    flex-wrap: wrap;
  }
  .pr-seg-btn {
    padding: 4px 8px;
    font-size: 10px;
  }
}
</style>

<style>
/* ── Global Tabulator overrides for Projects Report ─────────── */
.pr-tabulator .tabulator-header {
  background: #f7f8fb;
  border-bottom: 1px solid #e2e4ea;
}
.pr-tabulator .tabulator-col {
  background: #f7f8fb;
  border-right: none;
}
.pr-tabulator .tabulator-col-title {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 10px;
  color: #545454;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.pr-tabulator .tabulator-row {
  border-bottom: 1px solid #f0f1f4;
  transition: background 100ms;
}
.pr-tabulator .tabulator-row:hover {
  background: #fafbfd;
}
.pr-tabulator .tabulator-row.tabulator-selected {
  background: #f4f7fe;
}
.pr-tabulator .tabulator-cell {
  border-right: none;
  font-size: 12px;
  color: #12283f;
  font-variant-numeric: tabular-nums;
  padding: 0 14px;
}
.pr-tabulator .tabulator-footer {
  background: #fff;
  border-top: 1px solid #e9e8e9;
  font-family: inherit;
  font-size: 12px;
  color: #545454;
  padding: 8px 14px;
}
.pr-tabulator .tabulator-page {
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 4px;
  color: #545454;
  font-size: 12px;
  padding: 4px 8px;
  margin: 0 2px;
  font-family: inherit;
}
.pr-tabulator .tabulator-page.active {
  background: #12283f;
  color: #fff;
  border-color: #12283f;
  font-weight: 600;
}
.pr-tabulator .tabulator-page:disabled {
  opacity: 0.4;
}

/* Cell custom elements */
.pr-proj-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pr-proj-thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #12283f;
}
.pr-proj-info {
  min-width: 0;
}
.pr-proj-name {
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283f;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.005em;
}
.pr-proj-name:hover {
  color: #fc5868;
  text-decoration: none;
}
.pr-proj-dev {
  font-size: 11px;
  color: #8a8a8a;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pr-type-tag {
  font-size: 11px;
  color: #12283f;
  padding: 2px 8px;
  border: 1px solid #e2e4ea;
  border-radius: 4px;
  background: #fff;
  white-space: nowrap;
}

.pr-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 8px;
  border-radius: 999px;
  font-weight: 500;
  font-size: 11px;
  white-space: nowrap;
}
.pr-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pr-sold-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 100px;
}
.pr-sold-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
}
.pr-sold-nums {
  font-size: 12px;
  color: #12283f;
  font-weight: 500;
}
.pr-sold-of {
  color: #8a8a8a;
}
.pr-sold-pct {
  font-size: 10px;
  color: #545454;
}
.pr-sold-track {
  height: 3px;
  background: #eef0f4;
  border-radius: 2px;
  overflow: hidden;
}
.pr-sold-bar {
  height: 100%;
  background: #12283f;
  border-radius: 2px;
  transition: width 200ms ease;
}

.pr-price {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: -0.005em;
}

.pr-placeholder {
  padding: 48px 24px;
  text-align: center;
  color: #8a8a8a;
  font-size: 13px;
  font-family: inherit;
}
</style>
